// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/ui-core/interaction-solid.tsx
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/ui/src/interaction/solid.tsx
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo tendly-app
//     node fleet/fleet.ts check --repo tendly-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * Solid bindings for the shared interaction context.
 *
 *   <InteractionProvider>   creates the context, mounts focus + keyboard
 *                           trackers, and exposes the store.
 *   useInteraction()        access the context (state + setters).
 *   useInteractionFlag()    contribute reactively to a flag (refcounted).
 */

import {
  type Accessor,
  createContext,
  createEffect,
  onCleanup,
  onMount,
  type ParentComponent,
  useContext,
} from "solid-js";

import {
  createInteractionContext,
  type InteractionContext,
  trackFocus,
  trackKeyboardActivity,
} from "./context";

const Ctx = createContext<InteractionContext | undefined>(undefined);

export const InteractionProvider: ParentComponent = (props) => {
  const ctx = createInteractionContext();

  onMount(() => {
    const stopFocus = trackFocus(ctx);
    const stopKeyboard = trackKeyboardActivity(ctx);
    onCleanup(() => {
      stopFocus();
      stopKeyboard();
    });
  });

  return <Ctx.Provider value={ctx}>{props.children}</Ctx.Provider>;
};

export function useInteraction(): InteractionContext {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useInteraction must be used within an <InteractionProvider>");
  }
  return ctx;
}

/**
 * Reactively contribute to one or more interaction flags. Each call owns its
 * contribution via a unique token, so multiple components may raise the same
 * flag without clobbering each other (refcounted in the context).
 */
export function useInteractionFlag(flags: Record<string, Accessor<boolean>>): void {
  const ctx = useContext(Ctx);
  if (!ctx) {
    return;
  }
  const tokens = new Map<string, symbol>();
  for (const [flag, accessor] of Object.entries(flags)) {
    const token = Symbol(`interaction:${flag}`);
    tokens.set(flag, token);
    createEffect(() => {
      ctx.contribute(flag, token, accessor());
    });
  }
  onCleanup(() => {
    for (const [flag, token] of tokens.entries()) {
      ctx.contribute(flag, token, false);
    }
  });
}
